import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, Typography, Checkbox } from '../../../../../../components';
import { AppstoreOutlined, FolderOpenOutlined } from '@ant-design/icons';
import '../CreateAndManageModal.scss';

const { Option } = Select;
const { Title, Text } = Typography;

/**
 * ManageModal - A modal for managing Move Groups and Applications assignments
 * Used to assign VMs to move groups and/or applications
 */

export const ManageModal = ({
    open,
    onCancel,
    onApply,
    moveGroups = [],
    applications = [],
    users = [],
    selectedRowKeys = [],
}) => {
    const [form] = Form.useForm();
    const [unassign, setUnassign] = useState(false);

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            form.resetFields();
            setUnassign(false);
        }
    }, [open, form]);

    const handleApply = async () => {
        try {
            const values = await form.validateFields();
            onApply?.({ ...values, unassign });
        } catch (error) {
            // If unassign is checked, we don't need form values
            if (unassign) {
                onApply?.({ unassign: true });
            }
        }
    };

    const handleUnassignChange = (e) => {
        setUnassign(e.target.checked);
        if (e.target.checked) {
            form.setFieldsValue({ moveGroup: undefined, application: undefined });
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setUnassign(false);
        onCancel();
    };

    const selectedCount = selectedRowKeys.length;

    return (
        <Modal
            className="create-manage-modal"
            title={
                <Title level={4} className="modal-title">
                    Manage Move Groups and Apps
                </Title>
            }
            open={open}
            onCancel={handleCancel}
            footer={null}
            width={400}
            destroyOnHidden
        >
            <Text type="secondary" className="modal-note">
                Assign {selectedCount > 0 ? `${selectedCount} selected` : ''} source(s) to Move Group or Application
            </Text>

            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    moveGroup: undefined,
                    resource: undefined,
                    date: null,
                    application: undefined,
                }}
            >
                {/* Move Group Selection */}
                <Form.Item
                    label={
                        <span>
                            <FolderOpenOutlined className="icon-with-label" /> Move Group
                        </span>
                    }
                    name="moveGroup"
                >
                    <Select
                        placeholder="Select a Move Group"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={unassign}
                    >
                        {moveGroups.map((group) => (
                            <Option key={group.value} value={group.value}>
                                {group.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Application Selection */}
                <Form.Item
                    label={
                        <span>
                            <AppstoreOutlined className="icon-with-label" /> Application
                        </span>
                    }
                    name="application"
                >
                    <Select
                        placeholder="Select an Application"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={unassign}
                    >
                        {applications.map((app) => (
                            <Option key={app.value} value={app.value}>
                                {app.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Note about applications */}
                <Text type="secondary" className="modal-note">
                    Note: A VM can only belong to one Move Group and one Application at a time.
                </Text>

                {/* Unassign option */}
                <div className="unassign-section" style={{ marginTop: 16 }}>
                    <Checkbox checked={unassign} onChange={handleUnassignChange}>
                        Unassign (Clear all)
                    </Checkbox>
                    <Text type="secondary" className="modal-note" style={{ display: 'block', marginTop: 4 }}>
                        Remove from any current groups or app.
                    </Text>
                </div>

                {/* Footer buttons */}
                <div className="button-group">
                    <Button shape="round" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button shape="round" type="primary" onClick={handleApply} disabled={selectedCount === 0}>
                        Apply
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};
